import React, { FC, useState } from 'react'
import CallToAction, { CallToActionType } from '../CallToAction'
import { FormProps } from '../Form'
import ComponentMap from '../ComponentMap'
import { IModule } from '../PageSection'
import { IContentModule } from '../ContentModule/types'
import { v4 as uuidv4 } from 'uuid'

// MUI components
import MuiDialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Close from '@tdcerhverv/parrotfish/dist/icons/Icons/Close.svg'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import { visuallyHidden } from '@mui/utils'
import CircularProgress from '@mui/material/CircularProgress'
import VideoPlayer from '../VideoPlayer'

export type DialogProps = Queries.DIALOGFragment & {
  trackingIdModule?: string
  onDialogOpen?: () => void
  onDialogClose?: () => void
  button?: CallToActionType
  content?: IContentModule | FormProps
}

const Dialog: FC<DialogProps> = ({ title, button, content, trackingIdModule, onDialogOpen, onDialogClose }) => {
  const [open, setOpen] = useState(false)
  const [dialogButtons, setDialogButtons] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitted] = useState<boolean>(false)
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const toggleOpen = () => {
    setOpen(prev => !prev)
    setDialogButtons(true)
  }

  const handleOpen = () => {
    onDialogOpen && onDialogOpen()
    return
  }

  const handleClose = () => {
    onDialogClose && onDialogClose()
    return
  }

  // form configuration
  const isForm = content?.internal?.type === 'ContentfulModuleForm'
  const newUUID = uuidv4()
  const formState = (data: boolean) => {
    setIsSubmitted(data)
  }
  const showDialogButtons = (data: boolean) => setDialogButtons(data)

  const { breakpoints } = useTheme()
  const styles = {
    root: {
      [breakpoints.down('sm')]: {
        '& .MuiDialog-paper': {
          alignItems: 'center',
          justifyContent: 'center',
          '& > div': {
            maxWidth: '48ch',
            width: '100%',
          },
        },
        '& .MuiDialogContent-root': {
          flexGrow: 'unset',
        },
      },
    },
    dialogContent: {
      padding: '24px',
      paddingBottom: '12px',
      '& > div': {
        height: 'auto',
        borderRadius: '6px',
      },
    },
    dialogActions: {
      padding: '24px',
      paddingTop: '10px',
      paddingBottom: '12px',
    },
  }

  return (
    <>
      <MuiDialog
        open={open}
        onClose={() => {
          toggleOpen()
          handleClose()
        }}
        fullScreen={isMobile}
        sx={isForm ? styles.root : {}}
      >
        <DialogTitle>
          {title && title}
          <IconButton onClick={toggleOpen}>
            <SvgIcon aria-label="Luk modalen" component={Close} viewBox="0 0 48 48" />
            <span style={visuallyHidden}>Luk formularen</span>
          </IconButton>
        </DialogTitle>
        {content && (
          <DialogContent sx={styles.dialogContent}>
            {content && (content as IContentModule).video && (
              <VideoPlayer url={(content as IContentModule).video?.file?.url || ''} controls playing />
            )}
            <ComponentMap
              {...content}
              internal={(content as IModule).internal}
              {...(isForm && {
                uuid: newUUID,
                formState: formState,
                isDialogForm: true,
                showDialogButtons: showDialogButtons,
              })}
            />
          </DialogContent>
        )}
        {(dialogButtons || isSubmitting) && (
          <DialogActions sx={styles.dialogActions}>
            <Button onClick={toggleOpen} {...(isForm ? { variant: 'text' } : {})}>
              {isForm ? 'Afbryd' : 'Luk'}
            </Button>
            {isForm && (
              <CallToAction
                {...(content as FormProps).submitButton}
                {...(isSubmitting &&
                  (content as FormProps).buttonTextSending && { buttonText: (content as FormProps).buttonTextSending })}
                form={newUUID}
                id={`submit-button-${(content as FormProps).submitButton?.contentful_id}`}
                type="submit"
                disabled={!!isSubmitting}
                startIcon={isSubmitting && <CircularProgress color="inherit" size={16} />}
              />
            )}
          </DialogActions>
        )}
      </MuiDialog>
      <CallToAction
        {...button}
        onClick={() => {
          setOpen(prev => !prev)
          handleOpen()
        }}
        trackingIdModule={trackingIdModule}
      />
    </>
  )
}

export default Dialog
